export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  screenWidth: number;
  screenHeight: number;
  orientation: 'portrait' | 'landscape';
  touchSupported: boolean;
  devicePixelRatio: number;
  platform: string;
}

export interface ResponsiveConfig {
  mobile: {
    width: number;
    height: number;
    fontSize: number;
    buttonSize: number;
    spacing: number;
  };
  tablet: {
    width: number;
    height: number;
    fontSize: number;
    buttonSize: number;
    spacing: number;
  };
  desktop: {
    width: number;
    height: number;
    fontSize: number;
    buttonSize: number;
    spacing: number;
  };
}

export class MobileUtils {
  private static deviceInfo: DeviceInfo | null = null;
  
  // Responsive configuration
  private static readonly RESPONSIVE_CONFIG: ResponsiveConfig = {
    mobile: {
      width: 360,
      height: 640,
      fontSize: 14,
      buttonSize: 44, // Minimum touch target size (44x44px)
      spacing: 12,
    },
    tablet: {
      width: 768,
      height: 1024,
      fontSize: 16,
      buttonSize: 48,
      spacing: 16,
    },
    desktop: {
      width: 1024,
      height: 768,
      fontSize: 18,
      buttonSize: 32,
      spacing: 20,
    },
  };

  // Touch gesture thresholds
  private static readonly TOUCH_CONFIG = {
    tapThreshold: 100, // ms
    longPressThreshold: 500, // ms
    swipeThreshold: 50, // pixels
    pinchThreshold: 10, // pixels
    doubleTapThreshold: 300, // ms
  };

  public static getDeviceInfo(): DeviceInfo {
    if (!this.deviceInfo) {
      this.detectDevice();
    }
    return this.deviceInfo!;
  }

  private static detectDevice(): void {
    const userAgent = navigator.userAgent.toLowerCase();
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    // Mobile detection
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    const tabletRegex = /ipad|android(?=.*mobile)|tablet/i;
    
    const isMobile = mobileRegex.test(userAgent) && screenWidth < 768;
    const isTablet = tabletRegex.test(userAgent) || (screenWidth >= 768 && screenWidth < 1024);
    const isDesktop = !isMobile && !isTablet;
    
    // Orientation detection
    const orientation = screenWidth > screenHeight ? 'landscape' : 'portrait';
    
    // Touch support detection
    const touchSupported = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Platform detection
    let platform = 'unknown';
    if (userAgent.includes('android')) platform = 'android';
    else if (userAgent.includes('iphone') || userAgent.includes('ipad')) platform = 'ios';
    else if (userAgent.includes('windows')) platform = 'windows';
    else if (userAgent.includes('mac')) platform = 'mac';
    else if (userAgent.includes('linux')) platform = 'linux';
    
    this.deviceInfo = {
      isMobile,
      isTablet,
      isDesktop,
      screenWidth,
      screenHeight,
      orientation,
      touchSupported,
      devicePixelRatio: window.devicePixelRatio || 1,
      platform,
    };
  }

  public static getResponsiveConfig(): ResponsiveConfig {
    return this.RESPONSIVE_CONFIG;
  }

  public static getCurrentConfig(): ResponsiveConfig[keyof ResponsiveConfig] {
    const device = this.getDeviceInfo();
    
    if (device.isMobile) {
      return this.RESPONSIVE_CONFIG.mobile;
    } else if (device.isTablet) {
      return this.RESPONSIVE_CONFIG.tablet;
    } else {
      return this.RESPONSIVE_CONFIG.desktop;
    }
  }

  // Get optimal game dimensions for current device
  public static getOptimalGameSize(): { width: number; height: number } {
    const device = this.getDeviceInfo();
    const config = this.getCurrentConfig();
    
    if (device.isMobile) {
      // For mobile, use full screen with safe area considerations
      const safeWidth = Math.min(device.screenWidth, 480);
      const safeHeight = Math.min(device.screenHeight, 854);
      
      if (device.orientation === 'landscape') {
        return { width: safeHeight, height: safeWidth };
      } else {
        return { width: safeWidth, height: safeHeight };
      }
    } else if (device.isTablet) {
      return { width: 768, height: 1024 };
    } else {
      return { width: 1024, height: 768 };
    }
  }

  // Scale UI elements based on device
  public static scaleForDevice(baseSize: number): number {
    const device = this.getDeviceInfo();
    const config = this.getCurrentConfig();
    
    if (device.isMobile) {
      return baseSize * 0.8;
    } else if (device.isTablet) {
      return baseSize * 0.9;
    } else {
      return baseSize;
    }
  }

  // Get font size for current device
  public static getFontSize(size: 'small' | 'medium' | 'large' | 'xlarge'): number {
    const config = this.getCurrentConfig();
    const baseFontSize = config.fontSize;
    
    switch (size) {
      case 'small': return Math.round(baseFontSize * 0.8);
      case 'medium': return baseFontSize;
      case 'large': return Math.round(baseFontSize * 1.2);
      case 'xlarge': return Math.round(baseFontSize * 1.5);
      default: return baseFontSize;
    }
  }

  // Get button size for current device
  public static getButtonSize(): number {
    return this.getCurrentConfig().buttonSize;
  }

  // Get spacing for current device
  public static getSpacing(multiplier: number = 1): number {
    return this.getCurrentConfig().spacing * multiplier;
  }

  // Check if device supports specific features
  public static supportsFeature(feature: 'touch' | 'accelerometer' | 'orientation' | 'vibration'): boolean {
    switch (feature) {
      case 'touch':
        return this.getDeviceInfo().touchSupported;
      case 'accelerometer':
        return 'DeviceMotionEvent' in window;
      case 'orientation':
        return 'DeviceOrientationEvent' in window;
      case 'vibration':
        return 'vibrate' in navigator;
      default:
        return false;
    }
  }

  // Vibrate device (if supported)
  public static vibrate(pattern: number | number[]): void {
    if (this.supportsFeature('vibration')) {
      navigator.vibrate(pattern);
    }
  }

  // Lock orientation (if supported)
  public static lockOrientation(orientation: 'portrait' | 'landscape'): void {
    const screen = window.screen as any;
    if (screen.orientation && screen.orientation.lock) {
      screen.orientation.lock(orientation);
    } else if (screen.lockOrientation) {
      screen.lockOrientation(orientation);
    }
  }

  // Show/hide virtual keyboard area
  public static adjustForVirtualKeyboard(scene: Phaser.Scene): void {
    const device = this.getDeviceInfo();
    if (!device.isMobile) return;
    
    // Detect virtual keyboard
    const initialViewportHeight = window.innerHeight;
    
    const handleResize = () => {
      const currentHeight = window.innerHeight;
      const keyboardHeight = initialViewportHeight - currentHeight;
      
      if (keyboardHeight > 100) { // Keyboard is likely open
        // Adjust scene viewport
        scene.cameras.main.setViewport(0, 0, window.innerWidth, currentHeight);
      } else {
        // Restore original viewport
        scene.cameras.main.setViewport(0, 0, window.innerWidth, initialViewportHeight);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup function
    // return () => {
    //   window.removeEventListener('resize', handleResize);
    // };
  }

  // Optimize game performance for mobile
  public static getPerformanceSettings(): {
    maxParticles: number;
    maxProjectiles: number;
    targetFPS: number;
    useObjectPooling: boolean;
    enableAntialiasing: boolean;
    enableShadows: boolean;
  } {
    const device = this.getDeviceInfo();
    
    if (device.isMobile) {
      return {
        maxParticles: 50,
        maxProjectiles: 20,
        targetFPS: 30,
        useObjectPooling: true,
        enableAntialiasing: false,
        enableShadows: false,
      };
    } else if (device.isTablet) {
      return {
        maxParticles: 100,
        maxProjectiles: 40,
        targetFPS: 45,
        useObjectPooling: true,
        enableAntialiasing: true,
        enableShadows: false,
      };
    } else {
      return {
        maxParticles: 200,
        maxProjectiles: 80,
        targetFPS: 60,
        useObjectPooling: true,
        enableAntialiasing: true,
        enableShadows: true,
      };
    }
  }

  // Create responsive text style
  public static createResponsiveTextStyle(options: {
    fontSize: 'small' | 'medium' | 'large' | 'xlarge';
    color?: string;
    fontFamily?: string;
    fontStyle?: string;
    align?: string;
    wordWrap?: boolean;
    wordWrapWidth?: number;
  }): Phaser.Types.GameObjects.Text.TextStyle {
    const device = this.getDeviceInfo();
    const fontSize = this.getFontSize(options.fontSize);
    
    return {
      fontSize: `${fontSize}px`,
      color: options.color || '#ffffff',
      fontFamily: options.fontFamily || 'Arial',
      fontStyle: options.fontStyle || 'normal',
      align: options.align || 'left',
      wordWrap: options.wordWrap ? {
        width: options.wordWrapWidth || (device.isMobile ? 300 : 400)
      } : undefined,
    };
  }

  // Create responsive button style
  public static createResponsiveButtonStyle(options: {
    backgroundColor?: string;
    hoverColor?: string;
    textColor?: string;
    fontSize?: 'small' | 'medium' | 'large';
    padding?: { x: number; y: number };
  }): {
    style: Phaser.Types.GameObjects.Text.TextStyle;
    size: number;
    padding: { x: number; y: number };
  } {
    const buttonSize = this.getButtonSize();
    const spacing = this.getSpacing();
    
    return {
      style: {
        fontSize: `${this.getFontSize(options.fontSize || 'medium')}px`,
        color: options.textColor || '#ffffff',
        backgroundColor: options.backgroundColor || '#4CAF50',
        fontFamily: 'Arial',
      },
      size: buttonSize,
      padding: options.padding || { x: spacing, y: spacing / 2 },
    };
  }

  // Handle safe area for devices with notches/rounded corners
  public static getSafeAreaInsets(): { top: number; bottom: number; left: number; right: number } {
    const device = this.getDeviceInfo();
    
    if (device.platform === 'ios' && device.isMobile) {
      // iOS safe area
      const style = getComputedStyle(document.documentElement);
      return {
        top: parseInt(style.getPropertyValue('--sat') || '0'),
        bottom: parseInt(style.getPropertyValue('--sab') || '0'),
        left: parseInt(style.getPropertyValue('--sal') || '0'),
        right: parseInt(style.getPropertyValue('--sar') || '0'),
      };
    } else if (device.platform === 'android' && device.isMobile) {
      // Android safe area (simplified)
      return {
        top: 24, // Status bar height
        bottom: 0,
        left: 0,
        right: 0,
      };
    }
    
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }

  // Update device info when orientation changes
  public static onOrientationChange(callback: (deviceInfo: DeviceInfo) => void): () => void {
    const handleOrientationChange = () => {
      // Small delay to ensure dimensions are updated
      setTimeout(() => {
        this.detectDevice();
        callback(this.getDeviceInfo());
      }, 100);
    };
    
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);
    
    // Return cleanup function
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }

  // Prevent default touch behaviors (like zoom, scroll)
  public static preventDefaultTouchBehaviors(): () => void {
    const preventDefault = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault(); // Prevent pinch zoom
      }
    };
    
    const preventContextMenu = (e: Event) => {
      e.preventDefault(); // Prevent context menu on long press
    };
    
    document.addEventListener('touchstart', preventDefault, { passive: false });
    document.addEventListener('touchmove', preventDefault, { passive: false });
    document.addEventListener('contextmenu', preventContextMenu);
    
    // Prevent zoom on double tap
    let lastTouchEnd = 0;
    document.addEventListener('touchend', (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    }, { passive: false });
    
    // Return cleanup function
    return () => {
      document.removeEventListener('touchstart', preventDefault);
      document.removeEventListener('touchmove', preventDefault);
      document.removeEventListener('contextmenu', preventContextMenu);
    };
  }

  // Add haptic feedback for mobile interactions
  public static hapticFeedback(type: 'light' | 'medium' | 'heavy' = 'light'): void {
    const device = this.getDeviceInfo();
    if (!device.isMobile) return;
    
    if (device.platform === 'ios' && (window as any).DeviceMotionEvent) {
      // iOS haptic feedback
      const pattern = type === 'light' ? [10] : type === 'medium' ? [20] : [50];
      this.vibrate(pattern);
    } else if (device.platform === 'android') {
      // Android vibration
      const duration = type === 'light' ? 10 : type === 'medium' ? 25 : 50;
      this.vibrate([duration]);
    }
  }

  // Initialize mobile-specific settings
  public static initializeMobileSettings(): () => void {
    const cleanupFunctions: (() => void)[] = [];
    
    // Prevent default touch behaviors
    cleanupFunctions.push(this.preventDefaultTouchBehaviors());
    
    // Add viewport meta tag for mobile
    const existingViewport = document.querySelector('meta[name="viewport"]');
    if (!existingViewport) {
      const viewport = document.createElement('meta');
      viewport.name = 'viewport';
      viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(viewport);
    }
    
    // Add mobile-specific CSS
    const mobileCSS = `
      html, body {
        margin: 0;
        padding: 0;
        overflow: hidden;
        touch-action: none;
        user-select: none;
        -webkit-user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }
      
      canvas {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
      }
    `;
    
    const style = document.createElement('style');
    style.textContent = mobileCSS;
    document.head.appendChild(style);
    
    // Return cleanup function
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }
} 